import { useState, useEffect, useMemo, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2, Building2, Target, Server, FileText, Briefcase, Shield, Lock, AlertTriangle, Users, Wallet, Sparkles } from 'lucide-react';
import MetierPage from './MetierPage';
import IncidentLogJournal, { type IncidentLogEntry } from './IncidentLogJournal';
import {
  createBesoin,
  updateBesoin,
  getBesoinById,
  getClient2026TestResponses,
} from '../services/besoinsService';
import { useAuth } from '../contexts/AuthContext';
import { getAllClients } from '../services/clientService';
import { getIntakeTemplate } from '../domain/besoins/templates';
import {
  CLIENT_2026_ALL_COL_DEFS,
  CLIENT_2026_KMI_RESPONSE_KEYS,
  CLIENT_2026_ROWS,
  hasClient2026QuestionResponse,
  mergeClient2026EmptyTestResponses,
  resolveClient2026Schema,
  stripClient2026UnscoredKmis,
} from '../domain/besoins/templates/prise-de-besoin-client-2026';
import {
  CLIENT_2026_ALL_COL_DEFS as CLIENT_2026_V3_ALL_COL_DEFS,
  CLIENT_2026_ROWS as CLIENT_2026_V3_ROWS,
  templatePriseDeBesoinClient2026 as templatePriseDeBesoinClient2026V3,
} from '../domain/besoins/templates/prise-de-besoin-client-2026-v3';
import {
  CLIENT_2026_ALL_COL_DEFS as LEGACY_CLIENT_2026_ALL_COL_DEFS,
  legacyTemplatePriseDeBesoinClient2026,
} from '../domain/besoins/templates/prise-de-besoin-client-2026-legacy';
import type { MetierBesoin } from '../domain/metier/metierTypes';
import type { ClientItem } from '../services/clientService';

interface PriseDeBesoinWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number; // ID du client par défaut (optionnel)
  templateId?: string; // ID du template utilisé (ex: 'pdti-standard-pme')
  templateVersion?: string; // Version du template (ex: '1.0.0')
  client2026Schema?: number; // Marqueur explicite du schéma pour un besoin existant
  besoinId?: number; // ID d'une prise de besoin existante à éditer (optionnel)
  onSuccess?: (besoinId: number) => void; // Callback après sauvegarde réussie
}

type StepKey = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Structure complète des questions avec toutes les options (identique à PRIsME_DFK)
export const QUESTIONS = {
  phase1: {
    title: 'Qualification Client',
    icon: Building2,
    questions: [
      {
        id: 'company_name',
        question: "Nom de l'entreprise",
        type: 'text' as const,
        required: true,
      },
      {
        id: 'company_type',
        question: "Type d'entreprise",
        type: 'select' as const,
        options: ['Startup / Entreprise en démarrage', 'PME établie', 'Entreprise en croissance', 'Organisation'],
        required: true,
      },
      {
        id: 'industry',
        question: "Industrie / Secteur d'activité",
        type: 'select' as const,
        options: [
          "Cabinet d'avocats / Juridique",
          'Cabinet comptable / CPA',
          'Services professionnels (Autre)',
          'Manufacturing / Production',
          'Commerce de détail',
          'Santé',
          'Finance / Assurance',
          'Technologies',
          'Construction',
          'Transport / Logistique',
          'Éducation',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'employees',
        question: "Nombre d'employés",
        type: 'select' as const,
        options: ['Moins de 25 employés', '25-49 employés', '50-100 employés', '100-250 employés', '250 employés et plus'],
        required: true,
      },
      {
        id: 'revenue',
        question: "Chiffre d'affaires annuel (approximatif)",
        type: 'select' as const,
        options: ['Moins de 1M$', '1M$ - 3M$', '3M$ - 10M$', '10M$ - 25M$', '25M$ - 50M$', '50M$ et plus'],
        required: false,
      },
      {
        id: 'it_maturity',
        question: 'Maturité TI actuelle',
        type: 'select' as const,
        options: [
          'Débutant - Infrastructure basique, peu de systèmes',
          'Intermédiaire - Systèmes en place, mais peu optimisés',
          'Avancé - Infrastructure solide, processus établis',
          'Expert - TI stratégique, innovation continue',
        ],
        required: true,
      },
      {
        id: 'it_team',
        question: 'Équipe TI actuelle',
        type: 'select' as const,
        options: [
          'Aucune équipe dédiée',
          '1-2 personnes',
          '3-5 personnes',
          '6-10 personnes',
          '10+ personnes',
          'Totalement externalisé',
        ],
        required: true,
      },
      {
        id: 'budget_mensuel_bz',
        question: 'Budget mensuel estimé pour BZ (services TI)',
        type: 'select' as const,
        options: [
          '1500$ - Généralement pour client petite taille (5-10 utilisateurs)',
          '2000$ - Généralement pour client moyenne taille (10-15 utilisateurs)',
          '3000$ - Généralement pour client moyenne taille (15-25 utilisateurs)',
          '4000$ - Généralement pour client moyenne-grande taille (25-40 utilisateurs)',
          '5000$ - Généralement pour client grande taille (40-60 utilisateurs)',
          '7500$ - Généralement pour client grande taille (60-100 utilisateurs)',
          '10000$+ - Généralement pour client très grande taille (100+ utilisateurs)',
          'À déterminer',
        ],
        required: false,
      },
    ],
  },
  phase2: {
    title: 'Contexte & Objectifs',
    icon: Target,
    questions: [
      {
        id: 'main_challenge',
        question: "Quel est votre principal défi TI actuellement ?",
        type: 'textarea' as const,
        placeholder: "Ex: Notre infrastructure est obsolète et cause des interruptions fréquentes...",
        required: true,
      },
      {
        id: 'business_goals',
        question: "Objectifs d'affaires pour les 12 prochains mois",
        type: 'multiselect' as const,
        options: [
          'Croissance des revenus',
          "Amélioration de l'efficacité opérationnelle",
          'Expansion géographique',
          'Nouveaux produits/services',
          'Transformation numérique',
          'Réduction des coûts',
          "Amélioration de l'expérience client",
          'Conformité réglementaire',
        ],
        required: true,
      },
      {
        id: 'it_goals',
        question: 'Quels sont vos objectifs TI prioritaires ?',
        type: 'multiselect' as const,
        options: [
          "Moderniser l'infrastructure",
          "Améliorer la sécurité",
          'Migration cloud',
          'Automatisation des processus',
          'Business Intelligence / Analytics',
          'Mobilité des employés',
          'Collaboration à distance',
          'Innovation / IA',
          'Réduire les temps d\'arrêt',
          'Optimiser les coûts TI',
        ],
        required: true,
      },
      {
        id: 'timeline',
        question: "Quel est votre horizon temporel ?",
        type: 'select' as const,
        options: ['Urgent (0-3 mois)', 'Court terme (3-6 mois)', 'Moyen terme (6-12 mois)', 'Long terme (1-2 ans)', 'Stratégique (2-5 ans)'],
        required: true,
      },
      {
        id: 'budget_range',
        question: 'Budget estimé pour ce projet',
        type: 'select' as const,
        options: ['Moins de 25K$', '25K$ - 50K$', '50K$ - 100K$', '100K$ - 250K$', '250K$ - 500K$', '500K$ et plus', 'À déterminer'],
        required: false,
      },
      {
        id: 'budget_3_5y_global',
        question: 'Enveloppe budgétaire approximative sur 3-5 ans (CAPEX+OPEX)',
        type: 'select' as const,
        options: [
          'Moins de 100K$',
          '100K$ - 250K$',
          '250K$ - 500K$',
          '500K$ - 1M$',
          '1M$ - 2M$',
          '2M$ et plus',
          'À déterminer',
        ],
        required: false,
      },
      {
        id: 'budget_pillars_comment',
        question: 'Orientations budgétaires par grand pilier (infra, sécu, apps, support, IA, etc.)',
        type: 'textarea' as const,
        placeholder:
          "Ex: 40% modernisation infra & cloud, 25% cybersécurité/Loi 25, 20% données & BI, 10% applications métiers, 5% innovation/IA…",
        required: false,
      },
      {
        id: 'success_metrics',
        question: 'Comment mesurerez-vous le succès de ce projet ?',
        type: 'textarea' as const,
        placeholder: "Ex: Réduction de 50% des temps d'arrêt, augmentation de 30% de la productivité...",
        required: true,
      },
    ],
  },
  phase3: {
    title: 'Besoins Techniques',
    icon: Server,
    questions: [
      {
        id: 'current_systems',
        question: 'Systèmes critiques actuels',
        type: 'multiselect' as const,
        options: [
          'ERP (SAP, Oracle, Dynamics, etc.)',
          'CRM (Salesforce, HubSpot, etc.)',
          'Comptabilité / Finance',
          'Gestion de projet',
          'RH / Paie',
          'E-commerce',
          'Inventory / Supply Chain',
          'Manufacturing Execution System (MES)',
          'Business Intelligence / Analytics',
          'Autre',
        ],
        required: true,
      },
      {
        id: 'infrastructure',
        question: 'Infrastructure actuelle',
        type: 'multiselect' as const,
        options: [
          'Serveurs on-premise',
          'Cloud public (AWS, Azure, GCP)',
          'Cloud privé',
          'Hybride (on-premise + cloud)',
          'Hébergement tiers',
          'SaaS uniquement',
        ],
        required: true,
      },
      {
        id: 'pain_points',
        question: 'Points de douleur principaux',
        type: 'multiselect' as const,
        options: [
          'Performance lente',
          'Pannes fréquentes',
          'Sécurité insuffisante',
          "Manque d'intégration entre systèmes",
          'Processus manuels trop nombreux',
          'Difficulté à obtenir des rapports',
          'Support utilisateur insuffisant',
          'Coûts TI trop élevés',
          'Technologie obsolète',
          'Manque de visibilité sur les opérations',
        ],
        required: true,
      },
      {
        id: 'priorities',
        question: 'Classez vos priorités (sélectionnez top 3)',
        type: 'multiselect' as const,
        maxSelections: 3,
        options: [
          'Sécurité & Conformité',
          'Performance & Disponibilité',
          'Innovation & Compétitivité',
          'Réduction des coûts',
          'Expérience utilisateur',
          'Scalabilité / Croissance',
          'Agilité & Flexibilité',
        ],
        required: true,
      },
      {
        id: 'security_concerns',
        question: 'Préoccupations de sécurité',
        type: 'multiselect' as const,
        options: [
          'Protection des données clients',
          'Conformité réglementaire (RGPD, HIPAA, etc.)',
          'Cyberattaques / Ransomware',
          'Accès non autorisés',
          'Perte de données',
          'Backup & Disaster Recovery',
          'Formation des employés à la sécurité',
          'Aucune préoccupation majeure',
        ],
        required: true,
      },
      {
        id: 'integration_needs',
        question: 'Besoins d\'intégration',
        type: 'textarea' as const,
        placeholder: 'Décrivez les systèmes qui doivent communiquer ensemble...',
        required: false,
      },
      {
        id: 'users_impacted',
        question: 'Nombre d\'utilisateurs impactés par ce projet',
        type: 'select' as const,
        options: ['1-10 utilisateurs', '11-25 utilisateurs', '26-50 utilisateurs', '51-100 utilisateurs', '100+ utilisateurs', 'Toute l\'entreprise'],
        required: true,
      },
    ],
  },
  phase4: {
    title: 'Gouvernance & Support',
    icon: FileText,
    questions: [
      {
        id: 'decision_makers',
        question: 'Qui sont les décideurs clés pour ce projet ?',
        type: 'textarea' as const,
        placeholder: 'Ex: PDG, Directeur des opérations, CFO...',
        required: true,
      },
      {
        id: 'stakeholders',
        question: 'Parties prenantes principales',
        type: 'multiselect' as const,
        options: [
          'Direction générale (C-Suite)',
          'Finances / Comptabilité',
          'Opérations / Production',
          'Ventes / Marketing',
          'RH',
          'Service client',
          'Tous les départements',
        ],
        required: true,
      },
      {
        id: 'change_readiness',
        question: "L'organisation est-elle prête au changement ?",
        type: 'select' as const,
        options: [
          'Très réticente - beaucoup de résistance attendue',
          'Modérément réticente - un peu de résistance',
          'Neutre - acceptation passive',
          'Ouverte - bonne volonté générale',
          'Très enthousiaste - demande active de changement',
        ],
        required: true,
      },
      {
        id: 'training_needs',
        question: 'Besoins de formation',
        type: 'select' as const,
        options: [
          'Formation minimale - utilisateurs autonomes',
          'Formation de base - quelques sessions',
          'Formation complète - programme structuré',
          'Formation continue - support à long terme',
        ],
        required: true,
      },
      {
        id: 'support_model',
        question: 'Modèle de support souhaité',
        type: 'select' as const,
        options: [
          'Support réactif - intervention sur demande',
          'Support proactif - surveillance et prévention',
          'Support dédié - ressource à temps plein',
          'Support hybride - mix des approches',
          'Support interne - notre équipe gère',
        ],
        required: true,
      },
      {
        id: 'additional_info',
        question: 'Informations additionnelles ou préoccupations',
        type: 'textarea' as const,
        placeholder: "Tout ce que nous devrions savoir d'autre...",
        required: false,
      },
    ],
  },
};

// Mock data pré-rempli pour la démonstration (non utilisé actuellement - supprimé)

// Mapping des icônes par nom
const iconMap: Record<string, any> = {
  Building2,
  Target,
  Server,
  FileText,
  Briefcase,
  Shield,
  Lock,
  AlertTriangle,
  Users,
  Wallet,
  CheckCircle2,
};

/** Tableau vierge pour un produit additionnel (phase 4 Autres produits) */
const DEFAULT_PRODUIT_TABLE = `| Catégorie | Item | Prix unitaire | Unités | Total |
| --- | --- | --- | --- | --- |
|  |  |  |  |  |`;

const AUTRES_PRODUITS_TYPES = ['Cybersécurité', 'Téléphonie', 'Programmation', "Solutions d'affaires"] as const;

function SyncedHorizontalScroll({
  children,
  stickyTop = 0,
  className,
}: {
  children: (contentRef: React.RefObject<HTMLDivElement>) => React.ReactNode;
  stickyTop?: number;
  className?: string;
}) {
  const topRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const top = topRef.current;
    const content = contentRef.current;
    const spacer = spacerRef.current;
    if (!top || !content || !spacer) return;

    const syncWidth = () => {
      spacer.style.width = `${content.scrollWidth}px`;
    };
    syncWidth();

    const ro = new ResizeObserver(() => syncWidth());
    ro.observe(content);

    const onTopScroll = () => {
      if (content.scrollLeft !== top.scrollLeft) content.scrollLeft = top.scrollLeft;
    };
    const onContentScroll = () => {
      if (top.scrollLeft !== content.scrollLeft) top.scrollLeft = content.scrollLeft;
    };
    top.addEventListener('scroll', onTopScroll, { passive: true });
    content.addEventListener('scroll', onContentScroll, { passive: true });

    return () => {
      ro.disconnect();
      top.removeEventListener('scroll', onTopScroll);
      content.removeEventListener('scroll', onContentScroll);
    };
  }, []);

  return (
    <div className={className}>
      <div
        ref={topRef}
        className="sticky z-10 bg-white border border-gray-200 rounded-lg overflow-x-auto"
        style={{ top: stickyTop, height: 14 }}
        aria-label="Défilement horizontal du tableau"
      >
        <div ref={spacerRef} style={{ height: 1 }} />
      </div>
      <div className="mt-2">{children(contentRef)}</div>
    </div>
  );
}

export default function PriseDeBesoinWizardModal({
  isOpen,
  onClose,
  clientId = 2,
  templateId = 'pdti-standard-pme',
  templateVersion = '1.0.0',
  client2026Schema,
  besoinId,
  onSuccess,
}: PriseDeBesoinWizardModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<StepKey>(1);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [existingMetadata, setExistingMetadata] = useState<Record<string, unknown>>({});
  const [metierBesoin, setMetierBesoin] = useState<MetierBesoin | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [nomBesoin, setNomBesoin] = useState<string>('');
  const [descriptionBesoin, setDescriptionBesoin] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<number | ''>(clientId || '');
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [showPriseClient, setShowPriseClient] = useState<boolean>(true);
  const [makeBy, setMakeBy] = useState<string>('');
  const [archived, setArchived] = useState<boolean>(false);
  const [autresProduitsType, setAutresProduitsType] = useState<string>('');
  const [colWidths2026, setColWidths2026] = useState<Record<number, number>>({});
  const [client2026MenuFor, setClient2026MenuFor] = useState<string | null>(null);
  const [examplesOpenByQuestionId, setExamplesOpenByQuestionId] = useState<Record<string, boolean>>({});
  const [isGeneratingTestResponses, setIsGeneratingTestResponses] = useState(false);
  const [testResponseFeedback, setTestResponseFeedback] = useState<string | null>(null);

  const colResizeStateRef = useRef<{
    colIdx: number;
    startX: number;
    startWidth: number;
  } | null>(null);

  const startColResize2026 = (colIdx: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths2026[colIdx] ?? 180;
    colResizeStateRef.current = { colIdx, startX, startWidth };

    const onMove = (ev: MouseEvent) => {
      const st = colResizeStateRef.current;
      if (!st) return;
      const next = Math.max(120, Math.min(900, st.startWidth + (ev.clientX - st.startX)));
      setColWidths2026((prev) => ({ ...prev, [st.colIdx]: next }));
    };
    const onUp = () => {
      colResizeStateRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const fillMockDataKmi = () => {
    // Dev-only: préremplit les questions KMI (échelle 0–5)
    if (!template || !['prisme-kmi-v1', 'prisme-kmi-cible-v1'].includes(templateId)) return;
    const next: Record<string, string | string[]> = {};
    const pick = <T,>(arr: T[], idx: number) => arr[idx % arr.length];
    const preferredSteps = [4, 3, 5, 2, 0];

    template.phases.forEach((phase, phaseIdx) => {
      phase.questions.forEach((q, qIdx) => {
        if (q.type !== 'select') return;
        const opts = Array.isArray(q.options) ? q.options : [];
        if (!opts.length) return;
        const wanted = preferredSteps[(phaseIdx + qIdx) % preferredSteps.length];
        const exact = opts.find((o) => {
          const t = String(o).trim();
          return t.startsWith(`${wanted} —`) || t.startsWith(`${wanted}—`);
        });
        next[q.id] = exact || pick(opts, qIdx);
      });
    });

    setResponses((prev) => ({ ...prev, ...next }));
  };

  const isClient2026Template = ['prise-de-besoin-client-2026', 'prise-de-besoin-client-2026-analyse'].includes(templateId);
  const resolvedClient2026Schema = resolveClient2026Schema({
    schema: client2026Schema,
    templateVersion,
    isExisting: Boolean(besoinId),
  });
  const canGenerateClient2026TestResponses =
    user?.email.trim().toLowerCase() === 'jean-michel.caouette@bzinc.ca'
    && templateId === 'prise-de-besoin-client-2026'
    && templateVersion === '3.1.0'
    && resolvedClient2026Schema === 3;

  const fillTestResponsesClient2026 = async () => {
    if (!canGenerateClient2026TestResponses || isGeneratingTestResponses) return;
    setIsGeneratingTestResponses(true);
    setTestResponseFeedback(null);
    try {
      const testResponses = await getClient2026TestResponses();
      setResponses((current) => mergeClient2026EmptyTestResponses(current, testResponses));
      setTestResponseFeedback('Les champs vides ont été remplis avec des réponses tests cohérentes. Vérifiez puis sauvegardez le formulaire avant de générer le document.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Impossible de générer les réponses tests';
      setTestResponseFeedback(message);
    } finally {
      setIsGeneratingTestResponses(false);
    }
  };

  // Charger le template
  const template = useMemo(() => {
    try {
      const isLegacyClient2026 = isClient2026Template && resolvedClient2026Schema === 1;
      if (isLegacyClient2026) {
        return {
          ...legacyTemplatePriseDeBesoinClient2026,
          id: templateId,
          documentTypeCode: templateId.endsWith('-analyse')
            ? 'PRISE_BESOIN_CLIENT_2026_ANALYSE'
            : 'PRISE_BESOIN_CLIENT_2026',
        };
      }
      if (isClient2026Template && resolvedClient2026Schema === 2) {
        return {
          ...templatePriseDeBesoinClient2026V3,
          id: templateId,
          documentTypeCode: templateId.endsWith('-analyse')
            ? 'PRISE_BESOIN_CLIENT_2026_ANALYSE'
            : 'PRISE_BESOIN_CLIENT_2026',
        };
      }
      return getIntakeTemplate(templateId);
    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      return null;
    }
  }, [isClient2026Template, resolvedClient2026Schema, templateId]);

  // Templates qui incluent la phase « Métier du client ».
  const hasMetierPhase = ['pdti-standard-pme', 'pdti-pme-v2', 'pdti-pme-v3'].includes(templateId);

  const wizardPhases = useMemo(() => {
    if (!template) return [];
    return template.phases;
  }, [template]);

  // Déterminer le nombre maximum d'étapes (phases du template + phase métier optionnelle)
  const maxSteps = useMemo(() => {
    if (!template) return 5; // Fallback sur QUESTIONS
    return wizardPhases.length + (hasMetierPhase ? 1 : 0);
  }, [template, wizardPhases.length, hasMetierPhase]);

  // Charger les données existantes au montage
  useEffect(() => {
    if (isOpen) {
      loadClients();
      if (besoinId) {
        loadExistingData();
      } else {
        const defaultResponses: Record<string, string | string[]> = {};

        if (template) {
          wizardPhases.forEach((phase) => {
            phase.questions.forEach((question) => {
              if (question.defaultValue !== undefined) {
                defaultResponses[question.id] = question.defaultValue as string | string[];
              }
            });
          });
        }

        // Réinitialiser le formulaire pour une nouvelle prise de besoin
        setResponses(defaultResponses);
        setExistingMetadata({});
        setMetierBesoin(null);
        setNomBesoin('');
        setDescriptionBesoin('');
        setSelectedClientId(clientId || '');
        setStep(1);
      }
    }
  }, [isOpen, besoinId, clientId, template, wizardPhases]);

  // Obtenir les informations de l'étape actuelle (DOIT être avant le return conditionnel)
  const currentPhaseInfo = useMemo(() => {
    if (!template) {
      // Fallback sur QUESTIONS pour compatibilité
      const stepTitles: Record<StepKey, string> = {
        1: 'Qualification Client',
        2: 'Contexte & Objectifs',
        3: 'Besoins Techniques',
        4: 'Gouvernance & Support',
        5: 'Prise de besoin – Métier du client',
        6: 'Étape 6',
        7: 'Étape 7',
      };
      const stepIcons = [Building2, Target, Server, FileText, Briefcase];
      const currentPhase = step < 5 ? QUESTIONS[`phase${step}` as keyof typeof QUESTIONS] : null;
      return {
        title: stepTitles[step] || '',
        icon: stepIcons[step - 1] || Building2,
        phase: currentPhase,
      };
    }

    // Utiliser le template
    if (step <= wizardPhases.length) {
      const phase = wizardPhases[step - 1];
      const Icon = phase.icon ? (iconMap[phase.icon] || Building2) : Building2;
      return {
        title: phase.title,
        icon: Icon,
        phase: phase,
        description: phase.description,
      };
    }

    // Phase métier (pdti-standard-pme et pdti-pme-v2)
    if (step === wizardPhases.length + 1 && hasMetierPhase) {
      return {
        title: 'Prise de besoin – Métier du client',
        icon: Briefcase,
        phase: null,
      };
    }

    return {
      title: '',
      icon: Building2,
      phase: null,
    };
  }, [template, wizardPhases, step, hasMetierPhase]);

  const loadClients = async () => {
    setIsLoadingClients(true);
    try {
      const clientsList = await getAllClients();
      // Filtrer les clients actifs et trier par ordre alphabétique
      const activeClients = clientsList
        .filter((c) => c.actif !== false)
        .sort((a, b) => {
          const nomA = a.nom?.toLowerCase() || '';
          const nomB = b.nom?.toLowerCase() || '';
          return nomA.localeCompare(nomB, 'fr', { sensitivity: 'base' });
        });
      setClients(activeClients);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const loadExistingData = async () => {
    if (!besoinId) return;
    setIsLoading(true);
    try {
      const data = await getBesoinById(besoinId);
      if (data) {
        setExistingMetadata(data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)
          ? { ...data.metadata } : {});
        setNomBesoin(data.nom || '');
        setDescriptionBesoin(data.description || '');
        setSelectedClientId(data.client_id || '');
        setShowPriseClient(data.showpriseclient ?? true);
        setMakeBy(data.makeby || '');
        setArchived(data.archived ?? false);
        
        // Charger les réponses de toutes les phases du template (ou phases 1-4 en fallback)
        const loadedResponses: Record<string, string | string[]> = {};

        if (template) {
          template.phases.forEach((_, index) => {
            const phaseKey = `phase${index + 1}_data` as keyof typeof data;
            const phaseData = data[phaseKey];
            if (phaseData) {
              Object.assign(loadedResponses, phaseData);
            }
          });
        } else {
          if (data.phase1_data) Object.assign(loadedResponses, data.phase1_data);
          if (data.phase2_data) Object.assign(loadedResponses, data.phase2_data);
          if (data.phase3_data) Object.assign(loadedResponses, data.phase3_data);
          if (data.phase4_data) Object.assign(loadedResponses, data.phase4_data);
        }

        setResponses(loadedResponses);
        
        // Charger le métier (pdti-standard-pme et pdti-pme-v2)
        if (hasMetierPhase && data.phase5_metier_data) {
          setMetierBesoin(data.phase5_metier_data as MetierBesoin);
        }
      }
    } catch (error) {
      console.error('Erreur chargement besoins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponse = (questionId: string, value: string | string[]) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const collectPhaseResponses = (questions: Array<{ id: string; followUp?: { id?: string } }>) => {
    const phaseData: Record<string, any> = {};
    let answeredCount = 0;

    questions.forEach((q) => {
      if (responses[q.id] !== undefined) {
        phaseData[q.id] = responses[q.id];
        answeredCount += 1;
      }

      // Prise de besoin client 2026: les champs sont stockés sous des clés dérivées
      // ex: client2026_m_a1_01__B / __F / __H / __I / KMI_* / __enabledCols
      if (isClient2026Template && typeof q.id === 'string' && q.id.startsWith('client2026_')) {
        if (responses[q.id] === undefined && hasClient2026QuestionResponse(q.id, responses)) answeredCount += 1;
        Object.keys(responses).forEach((k) => {
          if (k.startsWith(`${q.id}__`) && responses[k] !== undefined) {
            phaseData[k] = responses[k];
          }
        });
      }

      const followUpId = q.followUp?.id || `${q.id}_followup`;
      if (responses[followUpId] !== undefined) {
        phaseData[followUpId] = responses[followUpId];
      }

      const otherId = `${q.id}_autre`;
      if (responses[otherId] !== undefined) {
        phaseData[otherId] = responses[otherId];
      }
    });

    return { phaseData, answeredCount };
  };

  // NOTE: Dans Gestion documentaire → Formulaires, on autorise les champs vides.
  // On ne bloque donc plus la navigation/sauvegarde sur les champs "required" des templates.

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Organiser les réponses par phase (template ou fallback QUESTIONS)
      const phaseDataMap: Record<number, Record<string, any>> = {};
      let totalQuestions = 0;
      let completedQuestions = 0;

      if (template && templateId !== 'pdti-standard-pme') {
        wizardPhases.forEach((phase, index) => {
          const { phaseData, answeredCount } = collectPhaseResponses(phase.questions);
          phaseDataMap[index + 1] = isClient2026Template && resolvedClient2026Schema === 3
            ? stripClient2026UnscoredKmis(phaseData)
            : phaseData;
          totalQuestions += phase.questions.length;
          completedQuestions += answeredCount;
        });
      } else {
        const p1 = collectPhaseResponses(QUESTIONS.phase1.questions);
        const p2 = collectPhaseResponses(QUESTIONS.phase2.questions);
        const p3 = collectPhaseResponses(QUESTIONS.phase3.questions);
        const p4 = collectPhaseResponses(QUESTIONS.phase4.questions);

        phaseDataMap[1] = p1.phaseData;
        phaseDataMap[2] = p2.phaseData;
        phaseDataMap[3] = p3.phaseData;
        phaseDataMap[4] = p4.phaseData;

        totalQuestions =
          QUESTIONS.phase1.questions.length +
          QUESTIONS.phase2.questions.length +
          QUESTIONS.phase3.questions.length +
          QUESTIONS.phase4.questions.length +
          (metierBesoin ? 1 : 0);

        completedQuestions =
          p1.answeredCount +
          p2.answeredCount +
          p3.answeredCount +
          p4.answeredCount +
          (metierBesoin ? 1 : 0);
      }

      const completionPercentage = totalQuestions > 0
        ? Math.round((completedQuestions / totalQuestions) * 100)
        : 0;

      // Déterminer les phases complétées
      const completedPhases: string[] = [];
      Object.entries(phaseDataMap).forEach(([phaseIndex, phaseData]) => {
        if (Object.keys(phaseData).length > 0) {
          completedPhases.push(`phase${phaseIndex}`);
        }
      });
      if (hasMetierPhase && metierBesoin) completedPhases.push('phase5_metier');

      // Valider que un client est sélectionné
      if (!selectedClientId || typeof selectedClientId !== 'number') {
        alert('Veuillez sélectionner un client');
        setIsSaving(false);
        return;
      }

      const besoinData = {
        client_id: selectedClientId as number,
        nom: nomBesoin || `Prise de besoin ${new Date().toLocaleDateString('fr-CA')}`,
        description: descriptionBesoin,
        template_id: templateId,
        template_version: templateVersion,
        ...Object.entries(phaseDataMap).reduce<Record<string, Record<string, any>>>((acc, [phaseIndex, phaseData]) => {
          if (Object.keys(phaseData).length > 0) {
            acc[`phase${phaseIndex}_data`] = phaseData;
          }
          return acc;
        }, {}),
        phase5_metier_data: hasMetierPhase ? (metierBesoin || undefined) : undefined,
        metier_id: hasMetierPhase ? (metierBesoin?.metierId || undefined) : undefined,
        statut: (completionPercentage === 100 ? 'complet' : 'en_cours') as 'complet' | 'en_cours',
        metadata: {
          ...existingMetadata,
          completed_phases: completedPhases,
          completion_percentage: completionPercentage,
          last_modified_phase: `phase${step}`,
          ...(isClient2026Template && resolvedClient2026Schema !== 1
            ? { client2026_schema: resolvedClient2026Schema } : {}),
        },
        showpriseclient: showPriseClient,
        makeby: makeBy || null,
        archived,
      };

      let savedBesoin;
      if (besoinId) {
        // Mise à jour
        savedBesoin = await updateBesoin(besoinId, besoinData);
      } else {
        // Création
        savedBesoin = await createBesoin(besoinData);
      }

      if (onSuccess && savedBesoin.id) {
        onSuccess(savedBesoin.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsSaving(false);
    }
  };

  const goNext = () => setStep((s) => (s < maxSteps ? ((s + 1) as StepKey) : s));
  const goPrev = () => setStep((s) => (s > 1 ? ((s - 1) as StepKey) : s));

  const stepTitles: Record<StepKey, string> = currentPhaseInfo.title ? { [step]: currentPhaseInfo.title } as any : {
    1: 'Qualification Client',
    2: 'Contexte & Objectifs',
    3: 'Besoins Techniques',
    4: 'Gouvernance & Support',
    5: 'Prise de besoin – Métier du client',
  };

  const stepIcons = [Building2, Target, Server, FileText, Briefcase];
  const currentPhase = currentPhaseInfo.phase;
  const currentPhaseData = currentPhase;

  const splitMarkdownRow = (row: string) => {
    const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map((cell) => cell.trim());
  };

  const isMarkdownSeparator = (line: string) =>
    /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line.trim());

  const parseMarkdownTable = (content: string) => {
    if (!content || !content.includes('|')) return null;
    const lines = content.split('\n').map((ln) => ln.trim());
    for (let i = 0; i < lines.length - 1; i += 1) {
      const headerLine = lines[i];
      const separatorLine = lines[i + 1];
      if (!headerLine.includes('|') || !isMarkdownSeparator(separatorLine)) continue;

      const headers = splitMarkdownRow(headerLine).filter((h) => h);
      if (headers.length < 2) continue;

      const rows: string[][] = [];
      for (let j = i + 2; j < lines.length; j += 1) {
        const rowLine = lines[j];
        if (!rowLine || !rowLine.includes('|')) break;
        const cells = splitMarkdownRow(rowLine);
        rows.push(cells);
      }

      return { headers, rows };
    }
    return null;
  };

  const normalizeHeader = (header: string) =>
    header
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const computeMonthlyTotalFromMarkdown = (markdown: string) => {
    const parsed = parseMarkdownTable(markdown);
    if (!parsed) return '';
    const totalIndex = parsed.headers.findIndex((h) => normalizeHeader(h).includes('total'));
    if (totalIndex === -1) return '';
    const sum = parsed.rows.reduce((acc, row) => {
      const raw = row[totalIndex] || '';
      const cleaned = raw.replace(/\s/g, '').replace('$', '').replace(',', '.');
      const val = parseFloat(cleaned);
      return Number.isFinite(val) ? acc + val : acc;
    }, 0);
    if (!Number.isFinite(sum) || sum <= 0) return '';
    return `${sum.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
  };

  const updatePricingTableValue = (markdown: string) => {
    const computedTotal = computeMonthlyTotalFromMarkdown(markdown);
    setResponses((prev) => ({
      ...prev,
      pricing_table: markdown,
      monthly_total: computedTotal || prev.monthly_total || '',
    }));
  };

  useEffect(() => {
    const pricingMarkdown = responses.pricing_table as string | undefined;
    if (!pricingMarkdown) return;
    const computedTotal = computeMonthlyTotalFromMarkdown(pricingMarkdown);
    if (computedTotal && computedTotal !== responses.monthly_total) {
      setResponses((prev) => ({
        ...prev,
        monthly_total: computedTotal,
      }));
    }
  }, [responses.pricing_table, responses.monthly_total]);

  const buildMarkdownTable = (headers: string[], rows: string[][]) => {
    const headerLine = `| ${headers.join(' | ')} |`;
    const separatorLine = `| ${headers.map(() => '---').join(' | ')} |`;
    const rowLines = rows.map((row) => {
      const normalized = headers.map((_, idx) => row[idx] || '');
      return `| ${normalized.join(' | ')} |`;
    });
    return [headerLine, separatorLine, ...rowLines].join('\n');
  };

  const isPricingColumn = (header: string) => {
    const normalized = normalizeHeader(header);
    return normalized.includes('prix unitaire') || normalized.includes('total');
  };

  /** Tableaux d'inclusion phase 2 (ODS) : détection et indices des colonnes "Inclus" / "Non inclus" */
  const getInclusionTableColumns = (questionId: string, headers: string[]) => {
    const isInclusionTable =
      questionId.startsWith('catalog_inclusions_') ||
      (headers.some((h) => normalizeHeader(h).includes('inclus')) && headers.some((h) => normalizeHeader(h).includes('non inclus')));
    if (!isInclusionTable) return null;
    const inclusColIndex = headers.findIndex((h) => normalizeHeader(h) === 'inclus' || (normalizeHeader(h).includes('inclus') && !normalizeHeader(h).includes('non')));
    const nonInclusColIndex = headers.findIndex((h) => normalizeHeader(h).includes('non inclus'));
    if (inclusColIndex === -1 || nonInclusColIndex === -1) return null;
    return { inclusColIndex, nonInclusColIndex };
  };

  /** Considère une cellule comme "inclus" si elle contient un check (✔️, ✓, etc.) */
  const isInclusionCellChecked = (cellValue: string) => {
    const v = (cellValue || '').trim();
    return /[\u2713\u2714\u2705✔️✓]/.test(v) || v.toLowerCase() === 'oui' || v === 'x';
  };

  const formatCurrencyCell = (rawValue: string) => {
    const cleaned = rawValue
      .replace(/\s/g, '')
      .replace(/\$/g, '')
      .replace(',', '.');
    const numeric = parseFloat(cleaned);
    if (!Number.isFinite(numeric)) return rawValue;
    return `${numeric.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
  };

  /** Parse la saisie utilisateur pour le stockage (nombre avec point décimal, ex. "25.00") */
  const parseCurrencyForStorage = (input: string): string => {
    const cleaned = String(input || '')
      .replace(/\s/g, '')
      .replace(/\$/g, '')
      .replace(',', '.');
    const num = parseFloat(cleaned);
    if (!Number.isFinite(num) || num < 0) return '';
    return num.toFixed(2);
  };

  /** Indices des colonnes Prix unitaire, Unités, Total pour le tableau de tarification */
  const getPricingColumnIndices = (headers: string[]) => {
    const unitPriceIndex = headers.findIndex(
      (h) => normalizeHeader(h).includes('prix') && normalizeHeader(h).includes('unitaire')
    );
    const unitsIndex = headers.findIndex((h) => normalizeHeader(h).includes('unites') || normalizeHeader(h).includes('unités'));
    const totalIndex = headers.findIndex((h) => normalizeHeader(h).includes('total'));
    return { unitPriceIndex, unitsIndex, totalIndex };
  };

  /** Calcule le total d'une ligne (Prix unitaire × Unités) et met à jour la ligne */
  const applyRowTotal = (
    row: string[],
    _headers: string[],
    { unitPriceIndex, unitsIndex, totalIndex }: { unitPriceIndex: number; unitsIndex: number; totalIndex: number }
  ): string[] => {
    if (unitPriceIndex < 0 || unitsIndex < 0 || totalIndex < 0) return row;
    const price = parseFloat(String(row[unitPriceIndex] || '').replace(',', '.'));
    const units = parseInt(String(row[unitsIndex] || '').replace(/\s/g, ''), 10);
    if (!Number.isFinite(price) || !Number.isFinite(units) || units < 0) return row;
    const total = (price * units).toFixed(2);
    const nextRow = [...row];
    nextRow[totalIndex] = total;
    return nextRow;
  };

  /** Rendu du tableau de tarification (phase 3 ou phase 4 produit) : catégories, ajout ligne/catégorie, totaux */
  const renderEditablePricingTable = (markdownValue: string, onTableChange: (nextMarkdown: string) => void, tableKey: string) => {
    const parsedTable = parseMarkdownTable(markdownValue);
    const categoryIndex = parsedTable
      ? parsedTable.headers.findIndex((h) => normalizeHeader(h).includes('categorie') || normalizeHeader(h).includes('category'))
      : -1;
    const groupedRows = parsedTable
      ? parsedTable.rows.reduce<{ type: 'group' | 'row'; label?: string; row?: string[] }[]>((acc, row) => {
          const categoryValue = categoryIndex >= 0 ? row[categoryIndex] || '' : '';
          const lastGroup = [...acc].reverse().find((item) => item.type === 'group') as { label?: string } | undefined;
          if (categoryIndex >= 0 && categoryValue && lastGroup?.label !== categoryValue) {
            acc.push({ type: 'group', label: categoryValue });
          }
          acc.push({ type: 'row', row });
          return acc;
        }, [])
      : [];

    if (!parsedTable) {
      return (
        <textarea
          value={markdownValue}
          onChange={(e) => onTableChange(e.target.value)}
          rows={6}
          placeholder="Tableau Markdown (| Catégorie | Item | Prix unitaire | Unités | Total |)"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-[11px] font-mono text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue resize-none"
        />
      );
    }

    const pricingIndicesRow = getPricingColumnIndices(parsedTable.headers);

    return (
      <div className="space-y-3">
        <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-[11px] text-gray-700">
            <thead className="bg-gray-50">
              <tr>
                {parsedTable.headers.map((header, idx) => (
                  <th key={`${tableKey}-th-${idx}`} className="px-3 py-2 text-left font-semibold border-b border-gray-200">
                    {header}
                  </th>
                ))}
                <th className="px-2 py-2 text-left font-semibold border-b border-gray-200 w-10" />
              </tr>
            </thead>
            <tbody>
              {groupedRows.map((item, rowIdx) => {
                if (item.type === 'group') {
                  return (
                    <tr key={`${tableKey}-group-${rowIdx}`} className="bg-gray-100">
                      <td colSpan={parsedTable.headers.length + 1} className="px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          {categoryIndex >= 0 ? (
                            <input
                              type="text"
                              value={item.label ?? ''}
                              onChange={(e) => {
                                const newLabel = e.target.value;
                                const nextRows = parsedTable.rows.map((r) => {
                                  if ((r[categoryIndex] || '') === (item.label ?? '')) {
                                    const next = [...r];
                                    next[categoryIndex] = newLabel;
                                    return next;
                                  }
                                  return r;
                                });
                                onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
                              }}
                              className="font-semibold text-gray-700 px-2 py-1 border border-gray-300 rounded bg-white min-w-[120px] text-[11px] focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                              placeholder="Nom de la catégorie"
                            />
                          ) : (
                            <span className="font-semibold text-gray-700">{item.label}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (!parsedTable || categoryIndex < 0) return;
                              const nextRows = [...parsedTable.rows];
                              let insertIndex = -1;
                              for (let idx = nextRows.length - 1; idx >= 0; idx -= 1) {
                                if ((nextRows[idx]?.[categoryIndex] || '') === item.label) {
                                  insertIndex = idx;
                                  break;
                                }
                              }
                              const newRow = parsedTable.headers.map(() => '');
                              newRow[categoryIndex] = item.label || '';
                              nextRows.splice(insertIndex >= 0 ? insertIndex + 1 : nextRows.length, 0, newRow);
                              onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
                            }}
                            className="inline-flex items-center px-2 py-1 text-[10px] font-semibold rounded-full border border-bz-blue/60 text-bz-blue bg-white hover:bg-bz-blue hover:text-white transition-colors"
                          >
                            Ajouter une ligne
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                const row = item.row || [];
                const originalIndex = parsedTable.rows.indexOf(row);
                const isCategoryColumn = categoryIndex >= 0;
                return (
                  <tr key={`${tableKey}-row-${rowIdx}`} className="border-b border-gray-100 last:border-b-0">
                    {parsedTable.headers.map((_, cellIdx) => {
                      if (isCategoryColumn && cellIdx === categoryIndex) {
                        return <td key={`${tableKey}-${rowIdx}-${cellIdx}`} className="px-2 py-2 align-top" />;
                      }
                      const isTotalCol = pricingIndicesRow && pricingIndicesRow.totalIndex >= 0 && cellIdx === pricingIndicesRow.totalIndex;
                      const isUnitPriceCol = pricingIndicesRow && pricingIndicesRow.unitPriceIndex >= 0 && cellIdx === pricingIndicesRow.unitPriceIndex;
                      const isUnitsCol = pricingIndicesRow && pricingIndicesRow.unitsIndex >= 0 && cellIdx === pricingIndicesRow.unitsIndex;
                      if (isTotalCol && pricingIndicesRow) {
                        const computedRow = applyRowTotal(row, parsedTable.headers, pricingIndicesRow);
                        const totalStr = computedRow[pricingIndicesRow.totalIndex] || '';
                        return (
                          <td key={`${tableKey}-${rowIdx}-${cellIdx}`} className="px-2 py-2 align-top">
                            <span className="block w-full px-2 py-1 text-[11px] text-gray-700">
                              {totalStr ? formatCurrencyCell(totalStr) : '—'}
                            </span>
                          </td>
                        );
                      }
                      const displayValue =
                        isUnitPriceCol ? (row[cellIdx] || '') :
                        isUnitsCol ? (row[cellIdx] || '').trim() :
                        isPricingColumn(parsedTable.headers[cellIdx] || '') ? formatCurrencyCell(row[cellIdx] || '') :
                        row[cellIdx] || '';
                      return (
                        <td key={`${tableKey}-${rowIdx}-${cellIdx}`} className="px-2 py-2 align-top">
                          <input
                            type="text"
                            inputMode={isUnitPriceCol || isUnitsCol ? 'decimal' : 'text'}
                            value={displayValue}
                            onChange={(e) => {
                              let nextValue: string;
                              if (isUnitPriceCol) nextValue = e.target.value;
                              else if (isUnitsCol) {
                                const v = e.target.value.replace(/\s/g, '');
                                const n = parseInt(v, 10);
                                nextValue = v === '' || !Number.isFinite(n) || n < 0 ? v : String(n);
                              } else if (isPricingColumn(parsedTable.headers[cellIdx] || '')) {
                                nextValue = parseCurrencyForStorage(e.target.value);
                              } else nextValue = e.target.value;
                              let nextRows = parsedTable.rows.map((r, idx) =>
                                idx === originalIndex ? r.map((c, cIdx) => (cIdx === cellIdx ? nextValue : c)) : r
                              );
                              if (pricingIndicesRow && (isUnitPriceCol || isUnitsCol)) {
                                nextRows = nextRows.map((r, idx) =>
                                  idx === originalIndex ? applyRowTotal(r, parsedTable.headers, pricingIndicesRow) : r
                                );
                              }
                              onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
                            }}
                            onBlur={
                              isUnitPriceCol && pricingIndicesRow
                                ? () => {
                                    const raw = row[cellIdx] || '';
                                    const normalized = parseCurrencyForStorage(raw);
                                    if (normalized === raw) return;
                                    let nextRows = parsedTable.rows.map((r, idx) =>
                                      idx === originalIndex ? r.map((c, cIdx) => (cIdx === cellIdx ? normalized : c)) : r
                                    );
                                    nextRows = nextRows.map((r, idx) =>
                                      idx === originalIndex ? applyRowTotal(r, parsedTable.headers, pricingIndicesRow) : r
                                    );
                                    onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
                                  }
                                : undefined
                            }
                            className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] text-gray-700 focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                          />
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 align-top w-10">
                      <button
                        type="button"
                        onClick={() => {
                          const nextRows = parsedTable.rows.filter((_, idx) => idx !== originalIndex);
                          onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
                        }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-red-500/60 text-red-600 hover:bg-red-50 transition-colors"
                        aria-label="Supprimer la ligne"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              const catIdx = parsedTable.headers.findIndex(
                (h) => normalizeHeader(h).includes('categorie') || normalizeHeader(h).includes('category')
              );
              const newRow = parsedTable.headers.map(() => '');
              if (catIdx >= 0) {
                const lastCategory = [...parsedTable.rows]
                  .reverse()
                  .map((r) => (r?.[catIdx] || '').trim())
                  .find((v) => v);
                if (lastCategory) {
                  newRow[catIdx] = lastCategory;
                }
              }
              const nextRows = [...parsedTable.rows, newRow];
              onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-bz-blue/60 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue hover:text-white transition-colors"
          >
            Ajouter une ligne
          </button>
          <button
            type="button"
            onClick={() => {
              const catIdx = parsedTable.headers.findIndex(
                (h) => normalizeHeader(h).includes('categorie') || normalizeHeader(h).includes('category')
              );
              const newRow = parsedTable.headers.map(() => '');
              if (catIdx >= 0) {
                const existingLabels = new Set(
                  parsedTable.rows.map((r) => (r[catIdx] || '').trim()).filter((v) => v.startsWith('Nouvelle catégorie'))
                );
                const num = existingLabels.size + 1;
                newRow[catIdx] = num === 1 ? 'Nouvelle catégorie' : `Nouvelle catégorie (${num})`;
              }
              const nextRows = [...parsedTable.rows, newRow];
              onTableChange(buildMarkdownTable(parsedTable.headers, nextRows));
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-bz-blue/60 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue hover:text-white transition-colors"
          >
            Ajouter une catégorie
          </button>
        </div>
      </div>
    );
  };

  const renderQuestion = (question: any) => {
    const value = responses[question.id] || '';
    const selectedValues = Array.isArray(value) ? value : [];
    const followUp = question.followUp;
    const followUpId = followUp?.id || `${question.id}_followup`;
    const followUpValue = (responses[followUpId] as string) || '';

    if (isClient2026Template && typeof question.id === 'string' && question.id.startsWith('client2026_')) {
      const keyB = `${question.id}__B`;
      const keyF = `${question.id}__F`;
      const keyH = `${question.id}__H`;
      const keyI = `${question.id}__I`;
      const keyEnabled = `${question.id}__enabledCols`;
      const rowDef = (resolvedClient2026Schema === 2 ? CLIENT_2026_V3_ROWS : CLIENT_2026_ROWS)
        .find((row) => row.id === question.id);
      const maturityScale = rowDef?.maturityScale;

      const bVal = (responses[keyB] as string) || '';
      const fVal = (responses[keyF] as string) || '';
      const hVal = (responses[keyH] as string) || '';
      const iVal = (responses[keyI] as string) || '';
      const enabledExtraCols = (responses[keyEnabled] as string[]) || [];

      const priorityOptions: Array<{ value: string; label: string }> = [
        { value: '1', label: '1 — Haute' },
        { value: '2', label: '2 — Moyennement haute' },
        { value: '3', label: '3 — Moyenne' },
        { value: '4', label: '4 — Moyennement basse' },
        { value: '5', label: '5 — Basse' },
      ];

      const normalizePriorityValue = (v: string): string => {
        const raw = String(v || '').trim();
        if (!raw) return '';
        const digit = raw.match(/[1-5]/)?.[0];
        if (digit && raw.length <= 3) return digit; // "3", "3 - Moyen", etc.
        const s = raw.toLowerCase();
        if (s.includes('moyennement haut') || s.includes('moyen haut')) return '2';
        if (s.includes('moyennement bas') || s.includes('moyennement basse') || s.includes('moyen bas')) return '4';
        if (s.includes('haut')) return '1';
        if (s.includes('basse') || s.includes('bas')) return '5';
        if (s.includes('moyen')) return '3';
        return digit || '';
      };

      const toggleExtraCol = (colKey: string) => {
        const cur = Array.isArray(responses[keyEnabled]) ? (responses[keyEnabled] as string[]) : [];
        const next = cur.includes(colKey) ? cur.filter((k) => k !== colKey) : [...cur, colKey];
        handleResponse(keyEnabled, next);
      };

      const textColumnKeys = new Set(['N', 'O', 'Q', 'R', 'S', 'EFFORT_DAYS']);
      const isLegacyClient2026 = resolvedClient2026Schema === 1;
      const extraColDefs: Array<{ key: string; label: string; kind: 'text' | 'textarea' }> = (
        isLegacyClient2026
          ? LEGACY_CLIENT_2026_ALL_COL_DEFS
              .filter((column) => !['A', 'B', 'C', 'D', 'E', 'F', 'H', 'I'].includes(column.key))
              .map((column) => ({ key: column.key, label: column.label }))
          : (resolvedClient2026Schema === 2 ? CLIENT_2026_V3_ALL_COL_DEFS : CLIENT_2026_ALL_COL_DEFS)
              .filter((column) => column.responseKey && !column.always)
              .map((column) => ({ key: column.responseKey!, label: column.label }))
      ).map((column) => ({
        ...column,
        kind: textColumnKeys.has(column.key) ? 'text' as const : 'textarea' as const,
      }));

      const selectAllExtraCols = () => {
        handleResponse(keyEnabled, extraColDefs.map((col) => col.key));
      };

      return (
        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{question.label}</div>
                {question.helpText && (
                  <div className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{question.helpText}</div>
                )}
              </div>
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setClient2026MenuFor((cur) => (cur === question.id ? null : question.id))}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50"
                >
                  Modifier
                </button>
                {client2026MenuFor === question.id && (
                  <div className="absolute right-0 mt-2 w-[340px] z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="text-xs font-semibold text-gray-800">Ajouter des cases (colonnes) à remplir</div>
                      <button
                        type="button"
                        onClick={selectAllExtraCols}
                        className="px-2 py-1 rounded-md border border-bz-blue text-bz-blue text-[11px] font-semibold hover:bg-blue-50"
                      >
                        Tout sélectionner
                      </button>
                    </div>
                    <div className="max-h-64 overflow-auto space-y-2 pr-1">
                      {extraColDefs.map((c) => {
                        const checked = enabledExtraCols.includes(c.key);
                        return (
                          <label key={c.key} className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleExtraCol(c.key)}
                              className="mt-0.5"
                            />
                            <span className="leading-4">{c.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setClient2026MenuFor(null)}
                        className="px-3 py-1.5 rounded-lg bg-bz-blue text-white text-xs font-semibold hover:bg-bz-dark"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {rowDef?.hasMaturity && maturityScale && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
                  Maturité du client
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {[
                    { responseKey: CLIENT_2026_KMI_RESPONSE_KEYS.asWas, label: 'KMI As-Was' },
                    { responseKey: CLIENT_2026_KMI_RESPONSE_KEYS.asIs, label: 'KMI As-Is' },
                    { responseKey: CLIENT_2026_KMI_RESPONSE_KEYS.toBe, label: 'KMI To-Be' },
                  ].map((field) => {
                    const maturityKey = `${question.id}__${field.responseKey}`;
                    const maturityValue = (responses[maturityKey] as string) || '';
                    return (
                      <div key={field.responseKey}>
                        <label className="mb-1 block text-[11px] font-semibold text-gray-700">{field.label}</label>
                        <select
                          value={maturityValue}
                          onChange={(event) => handleResponse(maturityKey, event.target.value)}
                          className="w-full rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">— Aucun score —</option>
                          {maturityScale.map((description, score) => (
                            <option key={score} value={String(score)}>
                              {description}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  STO
                </label>
                <select
                  value={bVal}
                  onChange={(e) => handleResponse(keyB, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                >
                  <option value="">—</option>
                  <option value="Stratégique">Stratégique</option>
                  <option value="Tactique">Tactique</option>
                  <option value="Opérationnel">Opérationnel</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Priorité client
                  </label>
                  <select
                    value={normalizePriorityValue(hVal)}
                    onChange={(e) => handleResponse(keyH, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="">—</option>
                    {priorityOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Priorité BZ
                  </label>
                  <select
                    value={normalizePriorityValue(iVal)}
                    onChange={(e) => handleResponse(keyI, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="">—</option>
                    {priorityOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Réponse du client
              </label>
              <textarea
                value={fVal}
                onChange={(e) => handleResponse(keyF, e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue resize-y"
                placeholder="-"
              />
            </div>

            {enabledExtraCols.length > 0 && (
              <div className="mt-4 grid grid-cols-1 gap-3">
                {extraColDefs
                  .filter((c) => enabledExtraCols.includes(c.key))
                  .map((c) => {
                    const k = `${question.id}__${c.key}`;
                    const v = (responses[k] as string) || '';
                    return (
                      <div key={c.key}>
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">{c.label}</label>
                        {c.kind === 'textarea' ? (
                          <textarea
                            value={v}
                            onChange={(e) => handleResponse(k, e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue resize-y"
                            placeholder="-"
                          />
                        ) : (
                          <input
                            type="text"
                            value={v}
                            onChange={(e) => handleResponse(k, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                            placeholder="-"
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Phase 4 ODS : Autres produits (type → ajouter → titre + tableau mensualité / frais migration)
    if (question.id === 'autres_produits') {
      type ProduitItem = { type: string; title: string; pricingTable: string; migrationFees: string };
      let list: ProduitItem[] = [];
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value || '[]') : value;
        list = Array.isArray(parsed) ? parsed : [];
      } catch {
        list = [];
      }
      const updateList = (next: ProduitItem[]) => handleResponse('autres_produits', JSON.stringify(next));
      const updateProduct = (index: number, updates: Partial<ProduitItem>) => {
        const next = list.map((p, i) => (i === index ? { ...p, ...updates } : p));
        updateList(next);
      };
      const addProduct = () => {
        if (!autresProduitsType) return;
        const next = [...list, { type: autresProduitsType, title: '', pricingTable: DEFAULT_PRODUIT_TABLE, migrationFees: '' }];
        updateList(next);
        setAutresProduitsType('');
      };
      const removeProduct = (index: number) => updateList(list.filter((_, i) => i !== index));

      return (
        <div className="space-y-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Type de produit ajouté</label>
              <select
                value={autresProduitsType}
                onChange={(e) => setAutresProduitsType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
              >
                <option value="">Sélectionnez un type...</option>
                {AUTRES_PRODUITS_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={addProduct}
              disabled={!autresProduitsType}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-bz-blue/60 text-bz-blue bg-white text-xs font-semibold hover:bg-bz-blue hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Ajouter
            </button>
          </div>

          {list.length === 0 && (
            <p className="text-xs text-gray-500 italic">Aucun produit ajouté. Choisissez un type et cliquez sur Ajouter.</p>
          )}

          {list.map((produit, index) => (
            <div key={index} className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50/50">
              <div className="px-4 py-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-800">{produit.type}</span>
                <button
                  type="button"
                  onClick={() => removeProduct(index)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-red-500/60 text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Supprimer ce produit"
                >
                  ×
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Titre du produit</label>
                  <input
                    type="text"
                    value={produit.title}
                    onChange={(e) => updateProduct(index, { title: e.target.value })}
                    placeholder="Ex: Audit cybersécurité, Système VoIP, Développement sur mesure..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tableau – Mensualité</label>
                  {renderEditablePricingTable(
                    produit.pricingTable,
                    (nextMarkdown) => updateProduct(index, { pricingTable: nextMarkdown }),
                    `produit-${index}`
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Frais de migration (uniques)</label>
                  <textarea
                    value={produit.migrationFees}
                    onChange={(e) => updateProduct(index, { migrationFees: e.target.value })}
                    rows={2}
                    placeholder="Ex: Frais de migration unique 800,00 $"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue resize-none"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (question.type === 'textarea') {
      const parsedTable = parseMarkdownTable(value as string);
      const categoryIndex = parsedTable
        ? parsedTable.headers.findIndex((h) => normalizeHeader(h).includes('categorie') || normalizeHeader(h).includes('category'))
        : -1;
      const isPriseBesoinClient2026Table = question.id === 'prise_besoin_client_2026_table';
      const isLockedColumn2026 = (header: string) => {
        const h = normalizeHeader(header);
        // Colonnes "gabarit" (verrouillées): on garde seulement les libellés/questions (et l'axe).
        // Le client doit pouvoir modifier les colonnes de qualification (Client N/E, STO).
        if (
          h === 'axe' ||
          h.includes('problemes')
        ) {
          return true;
        }
        return false;
      };

      const fillMockData2026 = () => {
        if (!parsedTable) return;
        const headers = parsedTable.headers || [];
        const headerIndex = (needle: string) => headers.findIndex((h) => normalizeHeader(h).includes(needle));
        const idxResponseClient = headerIndex('reponses du client');
        const idxHowFixed = headerIndex('comment on sait');
        const idxPrioClient = headerIndex('priorite selon client');
        const idxPrioBz = headerIndex('priorite selon directeur');
        const idxSmart = headerIndex('objectifs smart');
        const idxSolTech = headerIndex('technologique');
        const idxSolProc = headerIndex('processus');
        const idxSolHuman = headerIndex('humain');
        const idxCostBz = headerIndex('couts');
        const idxCostClient = headerIndex('cout charge');
        const idxTime = headerIndex('contraintes de temps');
        const idxNextYear = headerIndex('prochaine annee');
        const idxExec = headerIndex('execution');
        const idxEffort = headerIndex('duree');
        const idxRisks = headerIndex('risques');
        const idxMitigation = headerIndex('mitigation');
        const idxBesoinsOnly = headers.findIndex((h) => normalizeHeader(h) === 'besoins');
        const idxSubvention = headers.findIndex((h) => normalizeHeader(h).includes('subvention'));

        const nextRows = parsedTable.rows.map((row, rIdx) => {
          const next = [...row];
          const set = (idx: number, val: string) => {
            if (idx < 0) return;
            const header = headers[idx] || '';
            if (isLockedColumn2026(header)) return;
            next[idx] = val;
          };
          // Valeurs de test (mock) — uniquement pour faciliter les essais UI
          const n = rIdx + 1;
          set(idxResponseClient, `Réponse test (${n})`);
          set(idxHowFixed, `Critère de succès (${n})`);
          set(idxPrioClient, String(((n - 1) % 5) + 1));
          set(idxPrioBz, String((((n + 1) - 1) % 5) + 1));
          set(idxSmart, `SMART (${n}): spécifique, mesurable, atteignable, réaliste, temporel`);
          set(idxSolTech, `Solution tech (${n})`);
          set(idxSolProc, `Solution processus (${n})`);
          set(idxSolHuman, `Solution humain (${n})`);
          set(idxCostBz, `${(n * 500).toString()}$`);
          set(idxCostClient, `${(n * 700).toString()}$`);
          set(idxTime, `T${((n - 1) % 4) + 1}`);
          set(idxNextYear, n % 2 === 0 ? 'Oui' : 'Non');
          set(idxExec, n % 2 === 0 ? 'Interne' : 'Externe');
          set(idxEffort, n % 3 === 0 ? 'Élevé' : n % 3 === 1 ? 'Faible' : 'Moyen');
          set(idxRisks, `Risque (${n})`);
          set(idxMitigation, `Mitigation (${n})`);
          set(idxBesoinsOnly, `Besoins test (${n})`);
          set(idxSubvention, `Subvention test (${n})`);
          return next;
        });

        handleResponse(question.id, buildMarkdownTable(headers, nextRows));
      };

      const groupedRows = parsedTable
        ? parsedTable.rows.reduce<{ type: 'group' | 'row'; label?: string; row?: string[] }[]>((acc, row) => {
            const categoryValue = categoryIndex >= 0 ? row[categoryIndex] || '' : '';
            const lastGroup = [...acc].reverse().find((item) => item.type === 'group') as { label?: string } | undefined;
            if (categoryIndex >= 0 && categoryValue && lastGroup?.label !== categoryValue) {
              acc.push({ type: 'group', label: categoryValue });
            }
            acc.push({ type: 'row', row });
            return acc;
          }, [])
        : [];

      return (
        <div className="space-y-3">
          {!parsedTable && (
            <textarea
              value={(value as string) || ''}
              onChange={(e) => handleResponse(question.id, e.target.value)}
              placeholder={question.placeholder}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
            />
          )}
          {parsedTable && (
            <div className="space-y-3">
              {isPriseBesoinClient2026Table && import.meta.env.DEV && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={fillMockData2026}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-300 text-amber-900 bg-amber-50 text-[11px] font-semibold hover:bg-amber-100 transition-colors"
                    title="Préremplit les colonnes éditables avec des données de test (dev seulement)"
                  >
                    Remplir (Mock)
                  </button>
                </div>
              )}
              <SyncedHorizontalScroll className="prisme-synced-xscroll">
                {(scrollRef) => (
                  <div ref={scrollRef} className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
                    <table className="w-full text-[11px] text-gray-700">
                  <thead className="bg-gray-50">
                    <tr>
                      {parsedTable.headers.map((header, idx) => {
                        const inclusionCols = getInclusionTableColumns(question.id, parsedTable.headers);
                        if (inclusionCols && idx === inclusionCols.nonInclusColIndex) return null;
                        if (inclusionCols && idx === inclusionCols.inclusColIndex) {
                          return (
                            <th key={`inclus-exclus-${idx}`} colSpan={2} className="px-3 py-2 text-left font-semibold border-b border-gray-200">
                              Inclus / Non inclus
                            </th>
                          );
                        }
                        return (
                          <th
                            key={`${header}-${idx}`}
                            className="px-3 py-2 text-left font-semibold border-b border-gray-200 relative"
                            style={
                              isPriseBesoinClient2026Table
                                ? { width: colWidths2026[idx] ? `${colWidths2026[idx]}px` : undefined, minWidth: 160 }
                                : undefined
                            }
                          >
                            <div className="pr-3">{header}</div>
                            {isPriseBesoinClient2026Table && (
                              <div
                                role="separator"
                                aria-orientation="vertical"
                                onMouseDown={(e) => startColResize2026(idx, e)}
                                className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none"
                                title="Glisser pour agrandir/réduire la colonne"
                              >
                                <div className="h-full w-[2px] bg-transparent hover:bg-gray-300 mx-auto" />
                              </div>
                            )}
                          </th>
                        );
                      })}
                      {!isPriseBesoinClient2026Table && (
                        <th className="px-2 py-2 text-left font-semibold border-b border-gray-200 w-10" />
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRows.map((item, rowIdx) => {
                      if (item.type === 'group') {
                        return (
                          <tr key={`group-${rowIdx}`} className="bg-gray-100">
                            <td colSpan={parsedTable.headers.length + 1} className="px-3 py-2">
                              <div className="flex items-center justify-between gap-2">
                                {question.id === 'pricing_table' && categoryIndex >= 0 ? (
                                  <input
                                    type="text"
                                    value={item.label ?? ''}
                                    onChange={(e) => {
                                      const newLabel = e.target.value;
                                      const nextRows = parsedTable.rows.map((r) => {
                                        if ((r[categoryIndex] || '') === (item.label ?? '')) {
                                          const next = [...r];
                                          next[categoryIndex] = newLabel;
                                          return next;
                                        }
                                        return r;
                                      });
                                      const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                                      updatePricingTableValue(nextTable);
                                    }}
                                    className="font-semibold text-gray-700 px-2 py-1 border border-gray-300 rounded bg-white min-w-[120px] text-[11px] focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                                    placeholder="Nom de la catégorie"
                                  />
                                ) : (
                                  <span className="font-semibold text-gray-700">{item.label}</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!parsedTable || categoryIndex < 0) return;
                                    const nextRows = [...parsedTable.rows];
                                    let insertIndex = -1;
                                    for (let idx = nextRows.length - 1; idx >= 0; idx -= 1) {
                                      const row = nextRows[idx];
                                      if ((row?.[categoryIndex] || '') === item.label) {
                                        insertIndex = idx;
                                        break;
                                      }
                                    }
                                    const newRow = parsedTable.headers.map(() => '');
                                    newRow[categoryIndex] = item.label || '';
                                    const targetIndex = insertIndex >= 0 ? insertIndex + 1 : nextRows.length;
                                    nextRows.splice(targetIndex, 0, newRow);
                                    const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                                    if (question.id === 'pricing_table') {
                                      updatePricingTableValue(nextTable);
                                    } else {
                                      handleResponse(question.id, nextTable);
                                    }
                                  }}
                                  className="inline-flex items-center px-2 py-1 text-[10px] font-semibold rounded-full border border-bz-blue/60 text-bz-blue bg-white hover:bg-bz-blue hover:text-white transition-colors"
                                >
                                  Ajouter une ligne
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      const row = item.row || [];
                      const originalIndex = parsedTable.rows.indexOf(row);
                      const isCategoryColumn = categoryIndex >= 0;
                      return (
                        <tr key={`row-${rowIdx}`} className="border-b border-gray-100 last:border-b-0">
                          {parsedTable.headers.map((_, cellIdx) => {
                            const hideCategoryCell = isCategoryColumn && cellIdx === categoryIndex;
                            if (hideCategoryCell) {
                              return <td key={`cell-${rowIdx}-${cellIdx}`} className="px-2 py-2 align-top" />;
                            }
                            const inclusionCols = getInclusionTableColumns(question.id, parsedTable.headers);
                            if (inclusionCols && cellIdx === inclusionCols.nonInclusColIndex) return null;
                            if (inclusionCols && cellIdx === inclusionCols.inclusColIndex) {
                              const inclusVal = (row[inclusionCols.inclusColIndex] || '').trim();
                              const nonInclusVal = (row[inclusionCols.nonInclusColIndex] || '').trim();
                              const isIncluded = isInclusionCellChecked(inclusVal) && nonInclusVal === '';
                              const setInclusion = (included: boolean) => {
                                const nextRows = parsedTable.rows.map((r, idx) =>
                                  idx === originalIndex
                                    ? r.map((c, cIdx) =>
                                        cIdx === inclusionCols.inclusColIndex ? (included ? '✔️' : '') : cIdx === inclusionCols.nonInclusColIndex ? (included ? '' : '❌') : c
                                      )
                                    : r
                                );
                                const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                                if (question.id === 'pricing_table') updatePricingTableValue(nextTable);
                                else handleResponse(question.id, nextTable);
                              };
                              return (
                                <td key={`cell-${rowIdx}-${cellIdx}`} colSpan={2} className="px-2 py-2 align-top">
                                  <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
                                    <button
                                      type="button"
                                      onClick={() => setInclusion(true)}
                                      className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${isIncluded ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                      Inclus
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setInclusion(false)}
                                      className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${!isIncluded ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                      Non inclus
                                    </button>
                                  </div>
                                </td>
                              );
                            }
                            const pricingIndicesRow =
                              question.id === 'pricing_table' ? getPricingColumnIndices(parsedTable.headers) : null;
                            const isTotalCol =
                              pricingIndicesRow &&
                              pricingIndicesRow.totalIndex >= 0 &&
                              cellIdx === pricingIndicesRow.totalIndex;
                            const isUnitPriceCol =
                              pricingIndicesRow &&
                              pricingIndicesRow.unitPriceIndex >= 0 &&
                              cellIdx === pricingIndicesRow.unitPriceIndex;
                            const isUnitsCol =
                              pricingIndicesRow &&
                              pricingIndicesRow.unitsIndex >= 0 &&
                              cellIdx === pricingIndicesRow.unitsIndex;
                            if (question.id === 'pricing_table' && isTotalCol && pricingIndicesRow) {
                              const computedRow = applyRowTotal(row, parsedTable.headers, pricingIndicesRow);
                              const totalStr = computedRow[pricingIndicesRow.totalIndex] || '';
                              return (
                                <td key={`cell-${rowIdx}-${cellIdx}`} className="px-2 py-2 align-top">
                                  <span className="block w-full px-2 py-1 text-[11px] text-gray-700">
                                    {totalStr ? formatCurrencyCell(totalStr) : '—'}
                                  </span>
                                </td>
                              );
                            }
                            const displayValue =
                              question.id === 'pricing_table' && isUnitPriceCol
                                ? (row[cellIdx] || '')
                                : question.id === 'pricing_table' && isUnitsCol
                                  ? (row[cellIdx] || '').trim()
                                  : question.id === 'pricing_table' && isPricingColumn(parsedTable.headers[cellIdx] || '')
                                    ? formatCurrencyCell(row[cellIdx] || '')
                                    : row[cellIdx] || '';
                            const isLocked2026 =
                              isPriseBesoinClient2026Table && isLockedColumn2026(parsedTable.headers[cellIdx] || '');
                            return (
                              <td key={`cell-${rowIdx}-${cellIdx}`} className="px-2 py-2 align-top">
                                {isLocked2026 ? (
                                  <div className="w-full px-2 py-1 text-[11px] text-gray-700 whitespace-pre-wrap">
                                    {displayValue || '-'}
                                  </div>
                                ) : (
                                  <textarea
                                    rows={1}
                                    value={displayValue}
                                    title={displayValue}
                                    onChange={(e) => {
                                      let nextValue: string;
                                      if (question.id === 'pricing_table' && isUnitPriceCol) {
                                        nextValue = e.target.value;
                                      } else if (question.id === 'pricing_table' && isUnitsCol) {
                                        const v = e.target.value.replace(/\s/g, '');
                                        const n = parseInt(v, 10);
                                        nextValue = v === '' || !Number.isFinite(n) || n < 0 ? v : String(n);
                                      } else if (
                                        question.id === 'pricing_table' &&
                                        isPricingColumn(parsedTable.headers[cellIdx] || '')
                                      ) {
                                        nextValue = parseCurrencyForStorage(e.target.value);
                                      } else {
                                        nextValue = e.target.value;
                                      }
                                      let nextRows = parsedTable.rows.map((r, idx) =>
                                        idx === originalIndex ? r.map((c, cIdx) => (cIdx === cellIdx ? nextValue : c)) : r
                                      );
                                      if (
                                        question.id === 'pricing_table' &&
                                        pricingIndicesRow &&
                                        (isUnitPriceCol || isUnitsCol)
                                      ) {
                                        nextRows = nextRows.map((r, idx) =>
                                          idx === originalIndex ? applyRowTotal(r, parsedTable.headers, pricingIndicesRow) : r
                                        );
                                      }
                                      const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                                      if (question.id === 'pricing_table') {
                                        updatePricingTableValue(nextTable);
                                      } else {
                                        handleResponse(question.id, nextTable);
                                      }
                                    }}
                                    onInput={(ev) => {
                                      const el = ev.currentTarget;
                                      el.style.height = 'auto';
                                      el.style.height = `${Math.min(220, el.scrollHeight)}px`;
                                    }}
                                    onFocus={(ev) => {
                                      const el = ev.currentTarget;
                                      el.style.height = 'auto';
                                      el.style.height = `${Math.min(220, el.scrollHeight)}px`;
                                    }}
                                    onBlur={
                                      question.id === 'pricing_table' && isUnitPriceCol && pricingIndicesRow
                                        ? () => {
                                            const raw = row[cellIdx] || '';
                                            const normalized = parseCurrencyForStorage(raw);
                                            if (normalized === raw) return;
                                            let nextRows = parsedTable.rows.map((r, idx) =>
                                              idx === originalIndex
                                                ? r.map((c, cIdx) => (cIdx === cellIdx ? normalized : c))
                                                : r
                                            );
                                            nextRows = nextRows.map((r, idx) =>
                                              idx === originalIndex
                                                ? applyRowTotal(r, parsedTable.headers, pricingIndicesRow)
                                                : r
                                            );
                                            const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                                            updatePricingTableValue(nextTable);
                                          }
                                        : undefined
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] text-gray-700 focus:ring-1 focus:ring-bz-blue focus:border-bz-blue resize-y leading-snug"
                                    style={
                                      isPriseBesoinClient2026Table
                                        ? { width: colWidths2026[cellIdx] ? `${colWidths2026[cellIdx]}px` : '100%' }
                                        : undefined
                                    }
                                  />
                                )}
                              </td>
                            );
                          })}
                          {!isPriseBesoinClient2026Table && (
                            <td className="px-2 py-2 align-top w-10">
                              <button
                                type="button"
                                onClick={() => {
                                  if (!parsedTable) return;
                                  const nextRows = parsedTable.rows.filter((_, idx) => idx !== originalIndex);
                                  const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                                  if (question.id === 'pricing_table') {
                                    updatePricingTableValue(nextTable);
                                  } else {
                                    handleResponse(question.id, nextTable);
                                  }
                                }}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-red-500/60 text-red-600 hover:bg-red-50 transition-colors"
                                aria-label="Supprimer la ligne"
                              >
                                ×
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                  </div>
                )}
              </SyncedHorizontalScroll>
              {!isPriseBesoinClient2026Table && (
                <button
                  type="button"
                  onClick={() => {
                    const catIdx =
                      question.id === 'pricing_table'
                        ? parsedTable.headers.findIndex((h) => normalizeHeader(h).includes('categorie') || normalizeHeader(h).includes('category'))
                        : -1;
                    const newRow = parsedTable.headers.map(() => '');
                    if (question.id === 'pricing_table' && catIdx >= 0) {
                      const existingLabels = new Set(
                        parsedTable.rows.map((r) => (r[catIdx] || '').trim()).filter((v) => v.startsWith('Nouvelle catégorie'))
                      );
                      const num = existingLabels.size + 1;
                      newRow[catIdx] = num === 1 ? 'Nouvelle catégorie' : `Nouvelle catégorie (${num})`;
                    }
                    const nextRows = [...parsedTable.rows, newRow];
                    const nextTable = buildMarkdownTable(parsedTable.headers, nextRows);
                    if (question.id === 'pricing_table') {
                      updatePricingTableValue(nextTable);
                    } else {
                      handleResponse(question.id, nextTable);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-bz-blue/60 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue hover:text-white transition-colors"
                >
                  {question.id === 'pricing_table' ? 'Ajouter une catégorie' : 'Ajouter une ligne'}
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    if (question.type === 'select') {
      const showOtherField = value === 'Autre';
      const otherValue = (responses[`${question.id}_autre`] as string) || '';
      const showFollowUp = followUp?.whenValue ? value === followUp.whenValue : false;
      return (
        <div className="space-y-2">
          <select
            value={(value as string) || ''}
            onChange={(e) => {
              const nextValue = e.target.value;
              handleResponse(question.id, nextValue);
              if (nextValue !== 'Autre') {
                handleResponse(`${question.id}_autre`, '');
              }
              if (!followUp?.whenValue || nextValue !== followUp.whenValue) {
                handleResponse(followUpId, '');
              }
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
          >
            <option value="">Sélectionnez...</option>
            {question.options?.map((opt: string) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {showOtherField && (
            <input
              type="text"
              value={otherValue}
              onChange={(e) => handleResponse(`${question.id}_autre`, e.target.value)}
              placeholder="Précisez..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
            />
          )}
          {showFollowUp && (
            followUp?.type === 'textarea' ? (
              <textarea
                value={followUpValue}
                onChange={(e) => handleResponse(followUpId, e.target.value)}
                placeholder={followUp?.placeholder || 'Expliquez...'}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
              />
            ) : (
              <input
                type="text"
                value={followUpValue}
                onChange={(e) => handleResponse(followUpId, e.target.value)}
                placeholder={followUp?.placeholder || 'Précisez...'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
              />
            )
          )}
        </div>
      );
    }

    if (question.type === 'multiselect') {
      const showOtherField = selectedValues.some((opt) => opt.toLowerCase().startsWith('autre'));
      const otherValue = (responses[`${question.id}_autre`] as string) || '';
      const showFollowUp = followUp?.whenValue
        ? selectedValues.includes(followUp.whenValue)
        : false;
      const subOptions = (question as { subOptions?: Record<string, string[]> }).subOptions;
      return (
        <div className="space-y-2">
          {question.options?.map((opt: string) => {
            const isSelected = selectedValues.includes(opt);
            const opts = subOptions?.[opt] || [];
            return (
              <div key={opt} className="space-y-1">
                <label
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    isSelected ? 'border-bz-blue bg-bz-blue/5' : 'border-gray-300 bg-white hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const current = selectedValues;
                      if (e.target.checked) {
                        const toAdd = opts.length ? [opt, ...opts] : [opt];
                        handleResponse(question.id, [...current, ...toAdd]);
                      } else {
                        const toRemove = new Set([opt, ...opts]);
                        const nextValues = current.filter((v) => !toRemove.has(v));
                        handleResponse(question.id, nextValues);
                        if (opt.toLowerCase().startsWith('autre')) {
                          handleResponse(`${question.id}_autre`, '');
                        }
                        if (followUp?.whenValue && !nextValues.includes(followUp.whenValue)) {
                          handleResponse(followUpId, '');
                        }
                      }
                    }}
                    className="mt-1"
                  />
                  <span className="text-xs text-gray-700">{opt}</span>
                </label>
                {isSelected && opts.length > 0 && (
                  <ul className="ml-6 pl-2 text-[11px] text-gray-600 list-none space-y-0.5 border-l-2 border-bz-blue/30">
                    {opts.map((sub: string) => (
                      <li key={sub}>→ {sub}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {(question as any).maxSelections && (
            <p className="text-xs text-gray-500 italic">Sélectionnez jusqu&apos;à {(question as any).maxSelections} options</p>
          )}
          {showOtherField && (
            <input
              type="text"
              value={otherValue}
              onChange={(e) => handleResponse(`${question.id}_autre`, e.target.value)}
              placeholder="Précisez..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
            />
          )}
          {showFollowUp && (
            followUp?.type === 'textarea' ? (
              <textarea
                value={followUpValue}
                onChange={(e) => handleResponse(followUpId, e.target.value)}
                placeholder={followUp?.placeholder || 'Expliquez...'}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
              />
            ) : (
              <input
                type="text"
                value={followUpValue}
                onChange={(e) => handleResponse(followUpId, e.target.value)}
                placeholder={followUp?.placeholder || 'Précisez...'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
              />
            )
          )}
        </div>
      );
    }

    if (question.type === 'incident-log') {
      const logValue = value as IncidentLogEntry[] | string | null | undefined;
      return (
        <IncidentLogJournal
          value={logValue}
          onChange={(entries: IncidentLogEntry[]) => {
            handleResponse(question.id, JSON.stringify(entries));
          }}
          placeholder={question.placeholder}
        />
      );
    }

    return (
      <input
        type="text"
        value={question.id === 'monthly_total' ? (responses.monthly_total as string) || '' : (value as string) || ''}
        onChange={(e) => {
          if (question.id === 'monthly_total') return;
          handleResponse(question.id, e.target.value);
        }}
        readOnly={question.id === 'monthly_total'}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue disabled:bg-gray-100"
      />
    );
  };

  const toggleExamples = (questionId: string) => {
    setExamplesOpenByQuestionId((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  };


  if (!isOpen) return null;

  const isFullScreenTemplate = isClient2026Template;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
      <div
        className={
          isFullScreenTemplate
            ? 'w-[98vw] h-[96vh] max-w-none max-h-none rounded-xl bg-white shadow-2xl flex flex-col overflow-hidden'
            : 'w-[80vw] max-w-[1400px] max-h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden'
        }
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-bz-blue to-indigo-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shadow-inner">
              {(() => {
                const Icon = template ? currentPhaseInfo.icon : stepIcons[step - 1];
                return Icon ? <Icon className="w-5 h-5" /> : null;
              })()}
            </div>
            <div>
              <h2 className="text-lg font-semibold">{template?.name ?? 'Prise de besoin TI – Plan Directeur'}</h2>
              <p className="text-[11px] text-white/80">
                Phase {step} sur {maxSteps} • {currentPhaseInfo.title || stepTitles[step] || ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-white/80" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4 pb-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
            <span>
              Étape {step} sur {maxSteps} • {currentPhaseInfo.title}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">{isLoading ? 'Chargement...' : 'Prise de besoin TI'}</span>
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 via-bz-blue to-indigo-600 transition-all"
              style={{ width: `${(step / maxSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-gray-50 text-sm text-gray-700">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-600">Chargement des données...</p>
            </div>
          ) : step === 1 ? (
            <>
              {/* Champs nom et description au début */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Informations générales</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Client <span className="text-red-500">*</span>
                    </label>
                    {isLoadingClients ? (
                      <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-xs text-gray-500">
                        Chargement des clients...
                      </div>
                    ) : (
                      <select
                        value={selectedClientId}
                        onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value, 10) : '')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                        required
                      >
                        <option value="">Sélectionnez un client...</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.nom}
                            {client.secteur ? ` (${client.secteur})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Nom de la prise de besoin <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={nomBesoin}
                      onChange={(e) => setNomBesoin(e.target.value)}
                      placeholder="Ex: Plan Directeur TI 2025"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Description (optionnel)
                    </label>
                    <textarea
                      value={descriptionBesoin}
                      onChange={(e) => setDescriptionBesoin(e.target.value)}
                      placeholder="Description de cette prise de besoin..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-gray-100 mt-2">
                    <div className="flex items-start gap-2">
                      <input
                        id="showpriseclient"
                        type="checkbox"
                        checked={showPriseClient}
                        onChange={(e) => setShowPriseClient(e.target.checked)}
                        className="mt-1 h-4 w-4 text-bz-blue border-gray-300 rounded"
                      />
                      <label htmlFor="showpriseclient" className="text-xs text-gray-700">
                        Afficher cette prise de besoin dans l&apos;espace client
                        <span className="block text-[10px] text-gray-400">
                          Quand cette option est activée, le client peut voir ce besoin dans son portail PRIsME.
                        </span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Prise de besoins réalisée par
                      </label>
                      <input
                        type="text"
                        value={makeBy}
                        onChange={(e) => setMakeBy(e.target.value)}
                        placeholder="Nom de la personne (ex: Daniel Bourassa)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                      />
                    </div>
                    <div className="flex items-start gap-2">
                      <input
                        id="archived"
                        type="checkbox"
                        checked={archived}
                        onChange={(e) => setArchived(e.target.checked)}
                        className="mt-1 h-4 w-4 text-red-500 border-gray-300 rounded"
                      />
                      <label htmlFor="archived" className="text-xs text-gray-700">
                        Archiver cette prise de besoin
                        <span className="block text-[10px] text-gray-400">
                          (utilisé pour masquer des prises anciennes sans les supprimer)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Phase 1 */}
              {currentPhaseData && (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-bz-blue to-indigo-600 rounded-xl flex items-center justify-center">
                        {(() => {
                          const Icon = currentPhaseInfo.icon;
                          return <Icon className="w-6 h-6 text-white" />;
                        })()}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-bz-blue uppercase">
                          Phase {step} sur {maxSteps}
                        </p>
                        <h3 className="text-base font-semibold text-gray-900">{currentPhaseInfo.title}</h3>
                        {currentPhaseInfo.description && (
                          <p className="text-xs text-gray-600 mt-1">{currentPhaseInfo.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-5">
                      {currentPhaseData.questions.map((q: any) => {
                        // Adapter la structure : template utilise 'label', QUESTIONS utilise 'question'
                        const questionLabelRaw = q.label || q.question || '';
                        const questionLabel = typeof questionLabelRaw === 'string'
                          ? questionLabelRaw.replace(/\s*\*$/, '')
                          : questionLabelRaw;
                        const questionId = q.id;
                        const questionType = q.type || 'text';
                        const questionOptions = q.options || [];
                        const questionPlaceholder = q.placeholder || '';
                        const questionRequired = q.required || false;
                        const questionHelpText = q.helpText;
                        const examplesOpen = Boolean(examplesOpenByQuestionId[String(questionId)]);

                        return (
                          <div key={questionId} className="space-y-2">
                            <div className="flex items-start justify-between gap-3">
                              <label className="block text-xs font-semibold text-gray-700">
                                {questionLabel}
                                {questionRequired && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              {questionHelpText && (
                                <button
                                  type="button"
                                  onClick={() => toggleExamples(String(questionId))}
                                  className="flex-shrink-0 px-2 py-1 rounded-md border border-gray-300 bg-white text-[10px] font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                  Exemples
                                </button>
                              )}
                            </div>
                            {questionHelpText && examplesOpen && (
                              <p className="text-[10px] text-gray-500 italic mb-1 whitespace-pre-wrap">{questionHelpText}</p>
                            )}
                            {renderQuestion({
                              id: questionId,
                              type: questionType,
                              options: questionOptions,
                              placeholder: questionPlaceholder,
                              required: questionRequired,
                              followUp: q.followUp,
                              subOptions: q.subOptions,
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : step === maxSteps && hasMetierPhase ? (
            <MetierPage
              initialMetierBesoin={metierBesoin}
              onMetierBesoinChange={setMetierBesoin}
            />
          ) : currentPhaseData ? (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-bz-blue to-indigo-600 rounded-xl flex items-center justify-center">
                    {(() => {
                      const Icon = currentPhaseInfo.icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-bz-blue uppercase">
                      Phase {step} sur {maxSteps}
                    </p>
                    <h3 className="text-base font-semibold text-gray-900">{currentPhaseInfo.title}</h3>
                    {currentPhaseInfo.description && (
                      <p className="text-xs text-gray-600 mt-1">{currentPhaseInfo.description}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-5">
                  {currentPhaseData.questions.map((q: any) => {
                    // Adapter la structure : template utilise 'label', QUESTIONS utilise 'question'
                    const questionLabelRaw = q.label || q.question || '';
                    const questionLabel = typeof questionLabelRaw === 'string'
                      ? questionLabelRaw.replace(/\s*\*$/, '')
                      : questionLabelRaw;
                    const questionId = q.id;
                    const questionType = q.type || 'text';
                    const questionOptions = q.options || [];
                    const questionPlaceholder = q.placeholder || '';
                    const questionRequired = q.required || false;
                    const questionHelpText = q.helpText;
                    const examplesOpen = Boolean(examplesOpenByQuestionId[String(questionId)]);

                    return (
                      <div key={questionId} className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <label className="block text-xs font-semibold text-gray-700">
                            {questionLabel}
                            {questionRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {questionHelpText && (
                            <button
                              type="button"
                              onClick={() => toggleExamples(String(questionId))}
                              className="flex-shrink-0 px-2 py-1 rounded-md border border-gray-300 bg-white text-[10px] font-semibold text-gray-700 hover:bg-gray-50"
                            >
                              Exemples
                            </button>
                          )}
                        </div>
                        {questionHelpText && examplesOpen && (
                          <p className="text-[10px] text-gray-500 italic mb-1 whitespace-pre-wrap">{questionHelpText}</p>
                        )}
                        {renderQuestion({
                          id: questionId,
                          type: questionType,
                          options: questionOptions,
                          placeholder: questionPlaceholder,
                          required: questionRequired,
                          followUp: q.followUp,
                          subOptions: q.subOptions,
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">Phase non trouvée</div>
          )}
        </div>

        {/* Footer nav */}
        <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between text-xs">
          <button
            onClick={step === 1 ? onClose : goPrev}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {step === 1 ? (
              <>
                <X className="w-4 h-4" />
                Fermer
              </>
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                Étape précédente
              </>
            )}
          </button>
          <div className="flex items-center gap-2">
            {canGenerateClient2026TestResponses && (
              <button
                type="button"
                onClick={fillTestResponsesClient2026}
                disabled={isGeneratingTestResponses}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-amber-300 text-amber-900 bg-amber-50 font-semibold hover:bg-amber-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Remplit uniquement les champs vides avec un scénario de réponses tests cohérent"
              >
                <Sparkles className="w-4 h-4" />
                {isGeneratingTestResponses ? 'Génération...' : 'Générer des réponses tests'}
              </button>
            )}
            {testResponseFeedback && canGenerateClient2026TestResponses && (
              <span className="max-w-64 text-right text-[11px] text-gray-600" role="status">
                {testResponseFeedback}
              </span>
            )}
            {['prisme-kmi-v1', 'prisme-kmi-cible-v1'].includes(templateId) && import.meta.env.DEV && (
              <button
                type="button"
                onClick={fillMockDataKmi}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-amber-300 text-amber-900 bg-amber-50 font-semibold hover:bg-amber-100 transition-colors"
                title="Préremplit les réponses KMI (échelle 0–5, dev seulement)"
              >
                Remplir (Mock)
              </button>
            )}
            {step < maxSteps && (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full border border-bz-blue text-bz-blue bg-white font-semibold hover:bg-blue-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder et fermer'}
                <CheckCircle2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={step === maxSteps ? handleSave : goNext}
              disabled={isSaving}
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-bz-blue text-white font-semibold hover:bg-bz-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {step === maxSteps ? (
                <>
                  {isSaving ? 'Sauvegarde...' : 'Sauvegarder et terminer'}
                  <CheckCircle2 className="w-4 h-4" />
                </>
              ) : (
                <>
                  Étape suivante
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
